// src/context/SocketContext.js
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { userService } from '../services';
import { Report } from '../models/Report';
import { formatReportTypeDisplayName } from '../models/ReportTypeDesignHelper';
import { isCapacitor, isElectron } from '../utils/platform';
import { useAuth } from './AuthContext';
import { createLogger } from '../services/Debug';
const SocketContext = createContext({});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

// Helper function to extract patient name from transcription
function extractPatientName(transcription) {
  if (!transcription) return 'Rapport';
  
  // Try to extract patient info from common patterns
  const ageMatch = transcription.match(/Patient\s+de\s+(\d+)\s+ans/i);
  if (ageMatch) {
    return `Patient ${ageMatch[1]} ans`;
  }
  
  const genderAgeMatch = transcription.match(/(?:Homme|Femme|Patient)\s+de\s+(\d+)\s+ans/i);
  if (genderAgeMatch) {
    return genderAgeMatch[0];
  }
  
  const nameMatch = transcription.match(/(?:M\.|Mme|Mr|Mrs|Monsieur|Madame)\s+([A-Z][a-z]+)/);
  if (nameMatch) {
    return nameMatch[0];
  }
  
  // Default: use first few words
  const words = transcription.split(' ').slice(0, 3).join(' ');
  return words.length > 20 ? words.substring(0, 20) + '...' : words;
}

export const SocketProvider = ({ children }) => {
  const log = createLogger('SocketProvider');
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [newReports, setNewReports] = useState([]);
  const [connectionError, setConnectionError] = useState(null);
  const [hasNewReports, setHasNewReports] = useState(false);
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const { isAuthenticated, authReady, profile, refreshProfile } = useAuth();
  
  // Socket.IO server URL - ensure it matches your backend configuration
  const SOCKET_SERVER_URL = (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '') : null) || 'http://localhost:3000';

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setSocket(null);
    setIsConnected(false);
    setNewReports([]);
    setHasNewReports(false);
    isConnectingRef.current = false;
  }, []);

  const initializeSocket = useCallback(async () => {
    // Skip Socket.IO initialization on Capacitor (mobile platforms)
    if (isCapacitor) return;
    // Only on Electron
    if (!isElectron) return;
    // Require auth
    if (!authReady || !isAuthenticated) return;

    if (isConnectingRef.current) return;
    if (socketRef.current?.connected) return;

    // Get auth token
    const token = await userService.getAccessToken();
    if (!token) {
      setConnectionError('No authentication token found');
      return;
    }

    isConnectingRef.current = true;

    const socketInstance = io(SOCKET_SERVER_URL, {
      // Explicit namespace - connect to root namespace
      path: '/socket.io/',
      
      // Authentication
      auth: {
        token: token // Send token in auth object as expected by backend
      },
      
      // Connection options
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      
      // Transport configuration - try websocket first, fallback to polling
      transports: ['websocket', 'polling'],
      upgrade: true,
      
      // Connection behavior
      autoConnect: true,
      forceNew: false,
      
      // Additional options for debugging
      forceBase64: false
    });

    socketRef.current = socketInstance;
    setSocket(socketInstance);

    socketInstance.on('connect', () => {
      setIsConnected(true);
      setConnectionError(null);
      isConnectingRef.current = false;
      
      // Send a ping to verify connection
      const pingData = {
        timestamp: new Date().toISOString(),
        socketId: socketInstance.id,
        transport: socketInstance.io?.engine?.transport?.name
      };
      
      socketInstance.emit('ping', pingData);
    });

    socketInstance.on('disconnect', (reason) => {
      setIsConnected(false);
      isConnectingRef.current = false;
      
      // Handle different disconnect reasons
      if (reason === 'io server disconnect') {
        // Server disconnected us, might be due to auth failure
        handleAuthFailure();
      } else if (reason === 'transport error') {
        setConnectionError('Connection transport error');
      }
    });

    socketInstance.on('connect_error', (error) => {
      log.error('Socket connection error:', error.message || error.description || 'Connection failed');
      
      setConnectionError(error.message || error.description || 'Connection failed');
      setIsConnected(false);
      isConnectingRef.current = false;
      
      // Handle specific error types
      if (error.message) {
        if (error.message.includes('Invalid namespace')) {
          log.error('Namespace error - check server configuration');
          setConnectionError('Server configuration error (Invalid namespace)');
        } else if (error.message.includes('Authentication required') || 
                   error.message.includes('Invalid token') ||
                   error.message.includes('Token expired') ||
                   error.message.includes('unauthorized') || 
                   error.message.includes('jwt')) {
          handleAuthFailure();
        } else if (error.message.includes('timeout')) {
          log.error('Connection timeout detected');
          setConnectionError('Connection timeout - server may be unreachable');
        }
      }
    });

    socketInstance.on('reconnect', (attemptNumber) => {
      // Socket reconnected successfully
    });

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      // Attempting to reconnect
    });

    socketInstance.on('reconnect_error', (error) => {
      log.error('Socket reconnection error:', error?.message || 'Unknown reconnection error');
    });

    socketInstance.on('reconnect_failed', () => {
      log.error('Socket reconnection failed after all attempts');
      setConnectionError('Failed to reconnect after multiple attempts');
    });

    // Handle pong response
    socketInstance.on('pong', (data) => {
      // Pong received - connection verified
    });

    // NEW UNIFIED NOTIFICATION SYSTEM - Listen for all notifications
    socketInstance.on('notification', (data) => {
      // Handle report data events (both processing and completed)
      if (data.type === 'report_data') {
        handleReportUpdate(data.report, data.syncDate);
      }
    });

    // Handle report updates from notification system
    const handleReportUpdate = (reportData, syncDate) => {
      if (!reportData || !reportData.id) {
        log.warn('Socket: Invalid report data received (missing ID)');
        return;
      }
      
      // Handle different report statuses
      switch (reportData.status) {
        case 'pending':
          handleReportPending(reportData, syncDate);
          break;
        case 'processing':
          handleReportProcessing(reportData, syncDate);
          break;
        case 'completed':
          handleReportCompleted(reportData, syncDate);
          break;
        case 'failed':
          handleReportFailed(reportData, syncDate);
          break;
        default:
          handleReportGeneric(reportData, syncDate);
      }
    };

    // Handle different report states
    const handleReportPending = (reportData, syncDate) => {
      const reportInstance = new Report(reportData);
      
      // Add extra UI properties
      reportInstance._displayName = extractPatientName(reportData.transcription) || `Rapport ${reportData.reportType}`;
      reportInstance._receivedAt = new Date().toISOString();
      reportInstance._syncDate = syncDate;
      
      updateReportInState(reportInstance, 'pending');
    };

    const handleReportProcessing = (reportData, syncDate) => {
      const reportInstance = new Report(reportData);
      
      // Add extra UI properties
      reportInstance._displayName = extractPatientName(reportData.transcription) || `Rapport ${reportData.reportType}`;
      reportInstance._receivedAt = reportInstance._receivedAt || new Date().toISOString();
      reportInstance._syncDate = syncDate;
      
      updateReportInState(reportInstance, 'processing');

    };

    const handleReportCompleted = (reportData, syncDate) => {
      const reportInstance = new Report(reportData);
      
      // Add extra UI properties
      reportInstance._displayName = extractPatientName(reportData.transcription) || `Rapport ${reportData.reportType}`;
      reportInstance._receivedAt = reportInstance._receivedAt || new Date().toISOString();
      reportInstance._syncDate = syncDate;
      
      updateReportInState(reportInstance, 'completed');
      
      // Show success notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Rapport terminé !', {
          body: `Votre ${formatReportTypeDisplayName(reportInstance.reportType)} est maintenant disponible`,
          icon: '/icon.png'
        });
      }
    };

    const handleReportFailed = (reportData, syncDate) => {
      const reportInstance = new Report(reportData);
      
      // Add extra UI properties
      reportInstance._displayName = extractPatientName(reportData.transcription) || `Rapport ${reportData.reportType}`;
      reportInstance._receivedAt = reportInstance._receivedAt || new Date().toISOString();
      reportInstance._syncDate = syncDate;
      
      updateReportInState(reportInstance, 'failed');
      
      // Show error notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Erreur de traitement', {
          body: `Échec du traitement de votre ${formatReportTypeDisplayName(reportInstance.reportType)}: ${reportData.errorMessage || 'Erreur inconnue'}`,
          icon: '/icon.png'
        });
      }
    };

    const handleReportGeneric = (reportData, syncDate) => {
      const reportInstance = new Report(reportData);
      
      // Add extra UI properties
      reportInstance._displayName = extractPatientName(reportData.transcription) || `Rapport ${reportData.reportType}`;
      reportInstance._receivedAt = reportInstance._receivedAt || new Date().toISOString();
      reportInstance._syncDate = syncDate;
      
      updateReportInState(reportInstance, 'generic');
    };

    // Update application state
    const updateReportInState = (reportInstance, updateType) => {
      setNewReports(prev => {
        const existingIndex = prev.findIndex(r => r.id === reportInstance.id);
        
        if (existingIndex >= 0) {
          // Update existing report
          const newReports = [...prev];
          newReports[existingIndex] = reportInstance;
          return newReports;
        } else {
          // Add new report
          return [reportInstance, ...prev];
        }
      });
      
      setHasNewReports(true);
    };

    // Handle auth errors from server
    socketInstance.on('error', (error) => {
      log.error('Socket error:', error?.message || error?.type || 'Unknown socket error');
      
      if (error.type === 'auth_error') {
        handleAuthFailure();
      }
    });

  }, [SOCKET_SERVER_URL, authReady, isAuthenticated]);

  const handleAuthFailure = useCallback(async () => {
    try {
      const newToken = await userService.profileToken();
      if (newToken && newToken.accessToken) {
        // Reconnect with new token
        if (socketRef.current) {
          socketRef.current.auth.token = newToken.accessToken;
          socketRef.current.connect();
        } else {
          // Reinitialize socket
          await initializeSocket();
        }
      } else {
        // Token profile failed, disconnect
        disconnect();
        setConnectionError('Authentication failed');
      }
    } catch (error) {
      log.error('Failed to refresh token:', error);
      disconnect();
      setConnectionError('Authentication failed');
    }
  }, [initializeSocket, disconnect]);

  const connect = useCallback(() => {
    // Skip on Capacitor platforms
    if (isCapacitor) {
      return;
    }
    
    // Skip if not Electron
    if (!isElectron) {
      return;
    }
    
    initializeSocket();
  }, [initializeSocket]);

  

  // Initialize socket when authenticated (but not on Capacitor)
  useEffect(() => {
    if (isCapacitor) return;
    if (!isElectron) return;
    if (!authReady) return;
    if (!isAuthenticated) return;
    initializeSocket();
  }, [initializeSocket, authReady, isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Monitor auth state: disconnect when user logs out
  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated && socketRef.current?.connected) {
      disconnect();
    }
  }, [authReady, isAuthenticated, disconnect]);

  const acknowledgeReport = useCallback((reportId) => {
    // Skip on Capacitor platforms
    if (isCapacitor) {
      return;
    }
    
    if (socketRef.current && isConnected) {
      const acknowledgeData = {
        reportId,
        acknowledgedAt: new Date().toISOString()
      };
      
      socketRef.current.emit('report:acknowledged', acknowledgeData);
    } else {
      log.warn('⚠️ Cannot acknowledge report - socket not connected:', {
        hasSocket: !!socketRef.current,
        isConnected,
        reportId
      });
    }
  }, [isConnected]);

  const requestReportRefresh = useCallback(() => {
    // Skip on Capacitor platforms
    if (isCapacitor) {
      return;
    }
    
    if (socketRef.current && isConnected) {
      const refreshData = {
        timestamp: new Date().toISOString()
      };
      
      socketRef.current.emit('reports:refresh:request', refreshData);
    } else {
      log.warn('⚠️ Cannot request refresh - socket not connected:', {
        hasSocket: !!socketRef.current,
        isConnected
      });
    }
  }, [isConnected]);

  const clearReports = useCallback(() => {
    setNewReports([]);
    setHasNewReports(false);
  }, []);

  const removeReport = useCallback((reportId) => {
    setNewReports(prev => prev.filter(r => r.id !== reportId));
  }, []);

  const getNewReportsAndClear = useCallback(() => {
    const reports = [...newReports];
    setHasNewReports(false);
    return reports;
  }, [newReports]);

  // Test connection function for debugging
  const testConnection = useCallback(() => {
    // Skip on Capacitor platforms
    if (isCapacitor) {
      return false;
    }
    
    if (socketRef.current && isConnected) {
      const testData = { 
        message: 'Test from frontend',
        timestamp: new Date().toISOString(),
        socketId: socketRef.current.id,
        transport: socketRef.current.io?.engine?.transport?.name
      };
      
      socketRef.current.emit('test', testData);
      return true;
    } else {
      log.warn('🧪 Cannot test - socket not connected:', {
        hasSocket: !!socketRef.current,
        isConnected,
        socketState: socketRef.current ? {
          id: socketRef.current.id,
          connected: socketRef.current.connected,
          disconnected: socketRef.current.disconnected
        } : null
      });
      return false;
    }
  }, [isConnected]);

  const value = {
    socket,
    isConnected,
    newReports,
    hasNewReports,
    connectionError,
    acknowledgeReport,
    requestReportRefresh,
    clearReports,
    removeReport,
    connect,
    disconnect,
    getNewReportsAndClear,
    testConnection,
    debug: {
      socketId: socket?.id,
      connected: socket?.connected,
      connecting: isConnectingRef.current,
      url: SOCKET_SERVER_URL,
      transport: socket?.io?.engine?.transport?.name,
      readyState: socket?.io?.engine?.readyState,
      error: connectionError,
      reportsCount: newReports.length,
      hasNewReports,
      platform: isCapacitor ? 'Capacitor (Mobile)' : (isElectron ? 'Electron' : 'Web'),
      socketDisabled: isCapacitor || !isElectron,
      isElectron,
      userPlan: profile?.planType || 'unknown'
    }
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};